import {
  ActivateListenerPayload,
  OptimizelyContext,
  OptimizelyProvider,
  createInstance,
  enums,
} from '@optimizely/react-sdk';
import { PropsWithChildren, useContext, useEffect, useState } from 'react';
import {
  analyticsCookieExists,
  createAnalyticsCookie,
  getAnalyticsCookie,
} from '../utils/analyticsCookie';

import { LoadingPage } from '../pages/Loading/LoadingPage';
import config from '../config';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { v4 } from 'uuid';
import { waitUntilHeapLoaded } from '../utils/heap';

export const optimizelyInstance = createInstance({
  sdkKey: config.optimizelySdkKey,
});

configureHeapIntegration();

export const CustomOptimizelyProvider = ({ children }: PropsWithChildren) => {
  const { accessToken } = useAuthenticatedUser();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    optimizelyInstance.onReady().then(() => {
      setIsReady(true);
    });
  }, []);

  return (
    <OptimizelyProvider
      optimizely={optimizelyInstance}
      user={{
        id: getUserId(accessToken?.guid),
        attributes: {
          isTestUser: document.cookie.includes('ui-test'),
        },
      }}
    >
      {isReady ? children : <LoadingPage />}
    </OptimizelyProvider>
  );
};

export function useOptimizely() {
  return useContext(OptimizelyContext);
}

function configureHeapIntegration() {
  optimizelyInstance.notificationCenter.addNotificationListener<
    ActivateListenerPayload
  >(enums.NOTIFICATION_TYPES.ACTIVATE, payload => {
    waitUntilHeapLoaded().then(() => {
      const experimentKey = `experiment_${payload.experiment.key}`;
      window.heap?.addUserProperties({
        [experimentKey]: payload.variation.key,
      });
    });
  });
}

function getUserId(loggedInUserId?: string) {
  if (!analyticsCookieExists()) {
    createAnalyticsCookie(loggedInUserId);
  }

  const { loggedOutGuid } = getAnalyticsCookie();

  return loggedOutGuid ?? v4();
}
